import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Grid, List, ChevronLeft, ChevronRight, Trash2, Users, Plus, X, UserPlus, UserMinus, AlertTriangle, Bug, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import Button from '../ui/Button';
import PageHeader from '../ui/PageHeader';
import { getUserContacts, removeUserContact, setContactRating, clearContactsCache, saveContactsToFile, loadContactsFromFile } from '../../services/userContactsService';
import { getUserGroups, getGroupById, getGroupName, removeMemberFromGroup, deleteGroup } from '../../services/groupsService';
import PeopleCard from '../PeopleCard';
import StarRating from '../StarRating';
import CreateGroupModal from './CreateGroupModal';
import AddMembersModal from './AddMembersModal';
import ConfirmModal from '../ui/ConfirmModal';
import { useTranslation } from '../../context/LanguageContext';

/**
 * My Contacts page - shows only user's saved contacts from database
 */
const MyContacts = () => {
    const navigate = useNavigate();
    const { t, language } = useTranslation();
    const [myContacts, setMyContacts] = useState([]);
    const [myGroups, setMyGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('cards');
    const [currentPage, setCurrentPage] = useState(1);
    const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
    const [showAddMembersModal, setShowAddMembersModal] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(() => {
        // Restore selected group from sessionStorage
        const saved = sessionStorage.getItem('selectedGroupFilter');
        return saved ? JSON.parse(saved) : null;
    });
    const [groupMembers, setGroupMembers] = useState([]);
    const [deleteConfirmModal, setDeleteConfirmModal] = useState({ isOpen: false, contact: null });
    const [deleteGroupModal, setDeleteGroupModal] = useState({ isOpen: false, group: null });
    const [showDebugPanel, setShowDebugPanel] = useState(false);
    const [localStorageContacts, setLocalStorageContacts] = useState([]);
    const [serverFileContacts, setServerFileContacts] = useState([]);
    const [debugLogs, setDebugLogs] = useState([]);

    const userEmail = localStorage.getItem('userEmail');
    // Check role from localStorage or from cabaza_user object
    let userRole = localStorage.getItem('userRole') || '';
    let isAdmin = false;
    try {
        const cabazaUser = JSON.parse(localStorage.getItem('cabaza_user') || '{}');
        if (!userRole) {
            userRole = cabazaUser.role || '';
        }
        // Use isAdmin field from cabaza_user (boolean or string or number)
        isAdmin = cabazaUser.isAdmin === true || cabazaUser.isAdmin === 'true' || cabazaUser.isAdmin === 1 || cabazaUser.isAdmin === '1';
        // Debug: log isAdmin value
        console.log('cabaza_user:', cabazaUser);
        console.log('cabaza_user.isAdmin:', cabazaUser.isAdmin, 'type:', typeof cabazaUser.isAdmin, '-> isAdmin:', isAdmin);
    } catch (e) {
        console.error('Error parsing cabaza_user:', e);
    }
    const userId = parseInt(localStorage.getItem('userId'), 10);

    // Function to refresh debug panel data
    const refreshDebugData = async () => {
        try {
            console.log('=== REFRESH DEBUG DATA ===');

            // Refresh localStorage display
            const data = localStorage.getItem('cabaza_user_contacts');
            const allContacts = data ? JSON.parse(data) : {};
            const userData = allContacts[userId];
            const localContacts = Array.isArray(userData) ? userData : (userData?.contacts || []);
            console.log('localStorage contacts:', localContacts.length);
            setLocalStorageContacts(localContacts);

            // Refresh server file display
            const userJson = localStorage.getItem('cabaza_user');
            console.log('cabaza_user raw:', userJson);

            if (userJson) {
                const user = JSON.parse(userJson);
                const peopleuuid = user.peopleUuid || user.peopleuuid;
                const storageKey = user.storageKey;
                console.log('peopleuuid:', peopleuuid);
                console.log('storageKey:', storageKey ? storageKey.substring(0, 16) + '...' : 'MISSING');

                if (peopleuuid && storageKey) {
                    console.log('Calling loadContactsFromFile...');
                    const serverContacts = await loadContactsFromFile(peopleuuid, storageKey);
                    console.log('Server file response:', serverContacts);
                    console.log('Server contacts count:', serverContacts?.length || 0);
                    setServerFileContacts(serverContacts || []);
                } else {
                    console.warn('Missing peopleuuid or storageKey - cannot load server file');
                    setServerFileContacts([]);
                }
            } else {
                console.warn('cabaza_user not found in localStorage');
                setServerFileContacts([]);
            }
        } catch (error) {
            console.error('Error refreshing debug data:', error);
        }
    };

    const loadMyContacts = async () => {
        setLoading(true);
        setError(null);
        try {
            // Get contacts directly from API (already filtered by owner)
            const contacts = await getUserContacts(userId, true); // force refresh
            console.log('MyContacts - loaded contacts:', contacts);
            setMyContacts(contacts);

            // Load user's groups from API
            const groups = await getUserGroups(userId);
            console.log('MyContacts - loaded groups:', groups);
            setMyGroups(groups);

            // Refresh debug panel if open
            if (showDebugPanel && isAdmin) {
                setTimeout(refreshDebugData, 500); // Small delay to ensure server sync is complete
            }
        } catch (err) {
            console.error('Error loading contacts:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) {
            loadMyContacts();
        }
    }, [userId]);

    // Listen for contact added event from AddContactModal
    useEffect(() => {
        const handleContactAdded = (event) => {
            const newContact = event.detail;
            if (newContact) {
                setMyContacts(prev => [...prev, newContact]);
                setLocalStorageContacts(prev => [...prev, newContact]);
            }
        };

        window.addEventListener('contactAdded', handleContactAdded);
        return () => window.removeEventListener('contactAdded', handleContactAdded);
    }, []);

    // Save selected group to sessionStorage when it changes
    useEffect(() => {
        if (selectedGroup) {
            sessionStorage.setItem('selectedGroupFilter', JSON.stringify(selectedGroup));
        } else {
            sessionStorage.removeItem('selectedGroupFilter');
        }
    }, [selectedGroup]);

    // Load group members when component mounts with a saved group filter
    useEffect(() => {
        const loadSavedGroupMembers = async () => {
            if (selectedGroup?.groupUuid) {
                try {
                    const groupDetails = await getGroupById(selectedGroup.groupUuid);
                    if (groupDetails?.members) {
                        setGroupMembers(groupDetails.members);
                    }
                } catch (err) {
                    console.error('Error loading saved group members:', err);
                    // If group no longer exists, clear the filter
                    setSelectedGroup(null);
                    setGroupMembers([]);
                }
            }
        };
        loadSavedGroupMembers();
    }, []);

    const handleRemoveContact = (contactId, e) => {
        e.stopPropagation();
        const contact = myContacts.find(c => c.id === contactId);
        if (contact) {
            setDeleteConfirmModal({ isOpen: true, contact });
        }
    };

    const confirmDeleteContact = async () => {
        if (!deleteConfirmModal.contact) return;
        const success = await removeUserContact(userId, deleteConfirmModal.contact.id);
        if (success) {
            const contactId = deleteConfirmModal.contact.id;
            setMyContacts(prev => prev.filter(c => c.id !== contactId));
            // Also update localStorage display
            setLocalStorageContacts(prev => prev.filter(c => c.id !== contactId));
        }
        setDeleteConfirmModal({ isOpen: false, contact: null });
    };

    const handleRatingChange = async (contactId, newRating) => {
        // Optimistic update
        setMyContacts(prev => prev.map(c =>
            c.id === contactId ? { ...c, rating: newRating } : c
        ));
        // Save to API
        await setContactRating(contactId, newRating);
    };

    // Filter and sort contacts: rating DESC, then name ASC
    const filteredContacts = useMemo(() => {
        let filtered = myContacts;

        // Apply group filter if a group is selected
        if (selectedGroup && groupMembers.length > 0) {
            // Use contactId from group members to match with contact id
            const memberContactIds = groupMembers.map(m => m.contactId || m.contactid || m.CONTACTID);
            console.log('DEBUG - selectedGroup:', selectedGroup);
            console.log('DEBUG - groupMembers:', groupMembers);
            console.log('DEBUG - memberContactIds:', memberContactIds);
            console.log('DEBUG - myContacts ids:', myContacts.map(c => c.id));
            filtered = filtered.filter(c => memberContactIds.includes(c.id));
            console.log('DEBUG - filtered contacts:', filtered);
        } else if (selectedGroup && groupMembers.length === 0) {
            // Group selected but no members - show empty
            console.log('DEBUG - selectedGroup but no members');
            filtered = [];
        }

        // Apply search filter
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(c =>
                `${c.firstName} ${c.lastName}`.toLowerCase().includes(search) ||
                c.email?.toLowerCase().includes(search) ||
                c.company?.toLowerCase().includes(search) ||
                c.phone?.includes(search)
            );
        }

        // Sort by rating (DESC) then by name (ASC)
        return [...filtered].sort((a, b) => {
            const ratingA = a.rating || 0;
            const ratingB = b.rating || 0;

            if (ratingB !== ratingA) {
                return ratingB - ratingA;
            }

            const nameA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
            const nameB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }, [myContacts, searchTerm, selectedGroup, groupMembers]);

    // Pagination
    const itemsPerPage = viewMode === 'cards' ? 10 : 20;
    const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentContacts = filteredContacts.slice(startIndex, endIndex);

    // Reset to page 1 when search or view mode changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, viewMode]);

    // Load localStorage and server contacts for debug panel
    useEffect(() => {
        if (showDebugPanel && isAdmin) {
            refreshDebugData();
        }
    }, [showDebugPanel, userId, myContacts, isAdmin]);

    const handleContactClick = (contact) => {
        // Navigate to the appropriate detail page based on contact type
        if (contact.peopleuuid || contact.peopleUuid) {
            // Public contact - navigate to Cabaza profile
            navigate(`/people/${contact.peopleuuid || contact.peopleUuid}`);
        } else {
            // Private contact - navigate to contact detail page
            navigate(`/contact/${contact.localuuid}`);
        }
    };

    // Get card styles based on contactType (PUBLIC = linked to people, PRIVATE = hidden profile, CONTACT = local only)
    const getCardStyles = (contactType, email) => {
        const isCurrentUser = email === userEmail;

        if (isCurrentUser) {
            return {
                container: 'bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400',
                badge: 'bg-yellow-500 text-white',
                badgeText: 'YOU',
                title: 'text-yellow-700'
            };
        }

        switch (contactType) {
            case 'PUBLIC':
                // Linked to a public people profile
                return {
                    container: 'bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-cabaza-purple',
                    badge: 'bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white',
                    badgeText: 'PUBLIC',
                    title: 'text-cabaza-purple'
                };
            case 'PRIVATE':
                // Linked profile has PRIVATE visibility
                return {
                    container: 'bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-400',
                    badge: 'bg-orange-500 text-white',
                    badgeText: 'PRIVATE',
                    title: 'text-orange-700'
                };
            case 'CONTACT':
            default:
                // Private contact (not linked to people)
                return {
                    container: 'bg-gray-50 border-2 border-gray-200',
                    badge: 'bg-gray-400 text-white',
                    badgeText: 'CONTACT',
                    title: 'text-gray-700'
                };
        }
    };

    const handleGroupCreated = async (group) => {
        try {
            const groups = await getUserGroups(userId);
            setMyGroups(groups);

            // Auto-select the newly created group
            const newGroup = groups.find(g => g.groupUuid === group.groupUuid) || group;
            setSelectedGroup(newGroup);

            // Load the group members
            const groupDetails = await getGroupById(group.groupUuid);
            if (groupDetails?.members) {
                setGroupMembers(groupDetails.members);
            }
        } catch (err) {
            setMyGroups(prev => [...prev, group]);
            setSelectedGroup(group);
            setGroupMembers([]);
        }
    };

    // Handle group click - select group and load its members
    const handleGroupClick = async (group) => {
        if (selectedGroup?.id === group.id) {
            // Deselect if clicking the same group
            setSelectedGroup(null);
            setGroupMembers([]);
        } else {
            setSelectedGroup(group);
            // Load group members from API
            try {
                const groupDetails = await getGroupById(group.groupUuid);
                if (groupDetails?.members) {
                    setGroupMembers(groupDetails.members);
                }
            } catch (err) {
                console.error('Error loading group members:', err);
                setGroupMembers([]);
            }
        }
    };

    // Clear group filter
    const clearGroupFilter = () => {
        setSelectedGroup(null);
        setGroupMembers([]);
    };

    // Handle add members to group
    const handleAddMembersClick = () => {
        if (selectedGroup) {
            setShowAddMembersModal(true);
        }
    };

    // Handle delete group - open confirmation modal
    const handleDeleteGroup = (group, e) => {
        e.stopPropagation();
        setDeleteGroupModal({ isOpen: true, group });
    };

    // Confirm delete group
    const confirmDeleteGroup = async () => {
        if (!deleteGroupModal.group) return;

        try {
            const success = await deleteGroup(deleteGroupModal.group.groupUuid);
            if (success) {
                // Refresh groups list
                const groups = await getUserGroups(userId);
                setMyGroups(groups);
                // Clear selection if deleted group was selected
                if (selectedGroup?.id === deleteGroupModal.group.id) {
                    setSelectedGroup(null);
                    setGroupMembers([]);
                }
            }
        } catch (err) {
            console.error('Error deleting group:', err);
        }
        setDeleteGroupModal({ isOpen: false, group: null });
    };

    // Handle members added callback
    const handleMembersAdded = async (count) => {
        if (count > 0) {
            // Refresh groups list to update member count
            const groups = await getUserGroups(userId);
            setMyGroups(groups);
            // Refresh group members
            if (selectedGroup) {
                const groupDetails = await getGroupById(selectedGroup.groupUuid);
                if (groupDetails?.members) {
                    setGroupMembers(groupDetails.members);
                }
                // Update selected group with new data
                const updatedGroup = groups.find(g => g.id === selectedGroup.id);
                if (updatedGroup) {
                    setSelectedGroup(updatedGroup);
                }
            }
        }
    };

    // Handle remove contact from group
    const handleRemoveFromGroup = async (contactId, e) => {
        e.stopPropagation();
        if (!selectedGroup) return;

        try {
            await removeMemberFromGroup(selectedGroup.groupUuid, contactId);
            // Refresh group members
            const groupDetails = await getGroupById(selectedGroup.groupUuid);
            if (groupDetails?.members) {
                setGroupMembers(groupDetails.members);
            }
            // Refresh groups to update member count
            const groups = await getUserGroups(userId);
            setMyGroups(groups);
            const updatedGroup = groups.find(g => g.id === selectedGroup.id);
            if (updatedGroup) {
                setSelectedGroup(updatedGroup);
            }
        } catch (err) {
            console.error('Error removing from group:', err);
        }
    };

    // Convert contact to people-like object for PeopleCard
    // Contact data comes from Peoples table via JOIN on peopleuuid (LEFT JOIN)
    // contactType: 'PUBLIC' if peopleuuid is set, 'CONTACT' otherwise
    // Handle both camelCase and lowercase keys from API
    const contactToPerson = (contact) => ({
        id: contact.id,
        localuuid: contact.localuuid,
        peopleUuid: contact.peopleuuid || contact.peopleUuid,
        firstName: contact.firstName || contact.firstname,
        lastName: contact.lastName || contact.lastname,
        email: contact.email,
        phone: contact.phone,
        gsm: contact.gsm,
        company: contact.company,
        position: contact.position,
        googleAddress: contact.googleAddress || contact.googleaddress,
        latitude: contact.latitude,
        longitude: contact.longitude,
        website: contact.website,
        description: contact.description,
        contactType: contact.contactType || contact.contacttype || (contact.peopleuuid ? 'PUBLIC' : 'CONTACT'),
        role: contact.contactType || contact.contacttype || (contact.peopleuuid ? 'PUBLIC' : 'CONTACT'), // for backward compatibility
        // Profile type and employment status for PUBLIC contacts
        profileTypeId: contact.profileTypeId || contact.profiletypeid,
        profileTypeCode: contact.profileTypeCode || contact.profiletypecode,
        profileTypeName_fr: contact.profileTypeName_fr || contact.profiletypename_fr,
        profileTypeName_en: contact.profileTypeName_en || contact.profiletypename_en,
        employmentStatus: contact.employmentStatus || contact.employmentstatus,
        // Sync-related fields
        profileRole: contact.profileRole || '',
        profileDeleted: contact.profileDeleted || false
    });

    return (
        <div className="bg-white">
            {/* Page Header */}
            <PageHeader
                moduleCode="CONTACTS"
                title={t.contacts?.title || 'Contacts'}
                subtitle={t.contacts?.subtitle || 'Gérez vos contacts et groupes'}
            />

            {/* Groups Section */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <Users className="w-5 h-5 text-cabaza-purple" />
                        {t.groups?.myGroups || 'My groups'}
                    </h3>
                    <Button onClick={() => setShowCreateGroupModal(true)} icon={Plus} size="sm">
                        {t.groups?.createGroup || 'Create group'}
                    </Button>
                </div>

                {myGroups.length === 0 ? (
                    <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 text-center">
                        {t.groups?.noGroups || 'No groups created. Create a group to organize your contacts!'}
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-3">
                        {myGroups.map((group, index) => {
                            const isSelected = selectedGroup?.id === group.id;
                            return (
                                <div
                                    key={group.id || group.groupUuid || index}
                                    onClick={() => handleGroupClick(group)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                                        isSelected
                                            ? 'bg-cabaza-purple text-white'
                                            : 'bg-cabaza-purple/10 text-cabaza-purple hover:bg-cabaza-purple/20'
                                    }`}
                                >
                                    <Users className="w-4 h-4" />
                                    <span className="font-medium">{getGroupName(group, language)}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        isSelected ? 'bg-white/20' : 'bg-cabaza-purple/20'
                                    }`}>
                                        {group.memberCount || group.members?.length || 0}
                                    </span>
                                    <button
                                        onClick={(e) => handleDeleteGroup(group, e)}
                                        className={`p-1 rounded-full transition-colors ${
                                            isSelected
                                                ? 'hover:bg-white/20 text-white'
                                                : 'hover:bg-red-100 text-red-500'
                                        }`}
                                        title={t.groups?.deleteGroup || 'Delete group'}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Divider */}
            <hr className="my-6 border-gray-200" />

            {/* Search and View Mode */}
            <div className="mb-8 flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1 flex flex-wrap items-center gap-2 border border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-cabaza-purple/20 focus-within:border-cabaza-purple transition-shadow bg-white px-3 py-2">
                    <Search className="text-gray-400 w-5 h-5 flex-shrink-0" />
                    {/* Group filter chip */}
                    {selectedGroup && (
                        <div className="flex items-center gap-1 bg-cabaza-purple text-white px-2 py-1 rounded text-sm flex-shrink-0">
                            <Users className="w-3 h-3" />
                            <span className="max-w-[80px] sm:max-w-[120px] truncate">{getGroupName(selectedGroup, language)}</span>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    clearGroupFilter();
                                }}
                                className="ml-1 hover:bg-white/20 rounded-full p-0.5"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    )}
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t.dashboard?.searchInContacts || 'Rechercher dans mes contacts...'}
                        className="flex-1 min-w-[100px] py-1 bg-transparent border-none focus:outline-none focus:ring-0"
                    />
                </div>
                <div className="flex gap-2">
                    {/* Add member button - only visible when a group is selected */}
                    {selectedGroup && (
                        <button
                            onClick={handleAddMembersClick}
                            className="p-3 rounded-lg bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white hover:opacity-90 transition-opacity"
                            title={t.groups?.addMember || 'Add member'}
                        >
                            <UserPlus className="w-5 h-5" />
                        </button>
                    )}
                    <button
                        onClick={() => setViewMode('cards')}
                        className={`p-3 rounded-lg transition-colors ${
                            viewMode === 'cards'
                                ? 'bg-cabaza-purple text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={t.dashboard?.cardView || 'Card view'}
                    >
                        <Grid className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setViewMode('list')}
                        className={`p-3 rounded-lg transition-colors ${
                            viewMode === 'list'
                                ? 'bg-cabaza-purple text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={t.dashboard?.listView || 'List view'}
                    >
                        <List className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Loading state */}
            {loading && (
                <div className="text-center py-12">
                    <div className="w-12 h-12 border-4 border-cabaza-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 text-lg">
                        {t.dashboard?.loading || 'Loading...'}
                    </p>
                </div>
            )}

            {/* Error state */}
            {error && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 text-center max-w-md mx-auto mb-8">
                    <p className="text-red-800 mb-4">
                        {t.profile?.loadingError || 'Loading error'}: {error}
                    </p>
                    <button
                        onClick={() => loadMyContacts()}
                        className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
                    >
                        {t.dashboard?.retry || 'Retry'}
                    </button>
                </div>
            )}

            {/* Contacts display */}
            {!loading && !error && (
                <>
                    {filteredContacts.length === 0 ? (
                        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                            <p className="text-gray-500 text-lg mb-4">
                                {myContacts.length === 0
                                    ? (t.dashboard?.noSavedContacts || 'No saved contacts. Go to "Search" to add contacts.')
                                    : (t.dashboard?.noContacts || 'No contacts found')}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Results count */}
                            <div className="mb-4 text-sm text-gray-500">
                                {filteredContacts.length} {t.dashboard?.contact || 'contact(s)'}
                                {totalPages > 1 && ` - ${t.dashboard?.page || 'Page'} ${currentPage}/${totalPages}`}
                            </div>

                            {/* Cards View */}
                            {viewMode === 'cards' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {currentContacts.map((contact) => (
                                        <PeopleCard
                                            key={contact.id}
                                            person={contactToPerson(contact)}
                                            onClick={() => handleContactClick(contact)}
                                            actionType="delete"
                                            onAction={(_, e) => handleRemoveContact(contact.id, e)}
                                            userEmail={userEmail}
                                            language={language}
                                            variant="dashboard"
                                            rating={contact.rating || 0}
                                            onRatingChange={(newRating) => handleRatingChange(contact.id, newRating)}
                                            onRemoveFromGroup={selectedGroup ? (_, e) => handleRemoveFromGroup(contact.id, e) : undefined}
                                            t={t}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* List View */}
                            {viewMode === 'list' && (
                                <div className="bg-white rounded-lg shadow-md overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.dashboard?.favorites || 'Favorites'}
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.dashboard?.name || 'Name'}
                                                </th>
                                                <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.dashboard?.company || 'Company'}
                                                </th>
                                                <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="hidden xl:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.dashboard?.phone || 'Phone'}
                                                </th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.dashboard?.actions || 'Actions'}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {currentContacts.map((contact) => {
                                                const contactType = contact.contactType || (contact.peopleuuid ? 'PUBLIC' : 'CONTACT');
                                                const styles = getCardStyles(contactType, contact.email);

                                                return (
                                                    <tr
                                                        key={contact.id}
                                                        className="hover:bg-gray-50 transition-colors"
                                                    >
                                                        <td className="px-4 py-4">
                                                            <StarRating
                                                                rating={contact.rating || 0}
                                                                onRatingChange={(newRating) => handleRatingChange(contact.id, newRating)}
                                                                size="sm"
                                                            />
                                                        </td>
                                                        <td
                                                            onClick={() => handleContactClick(contact)}
                                                            className="px-6 py-4 cursor-pointer"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <span className={`text-sm font-medium ${styles.title}`}>
                                                                    {contact.firstName} {contact.lastName}
                                                                </span>
                                                                {contactType === 'PRIVATE' && (
                                                                    <span className="px-1.5 py-0.5 text-xs font-medium bg-orange-500 text-white rounded">
                                                                        PRIVATE
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {contact.position && (
                                                                <div className="text-xs text-gray-500">{contact.position}</div>
                                                            )}
                                                        </td>
                                                        <td className="hidden md:table-cell px-6 py-4">
                                                            <div className="text-sm text-gray-600">{contact.company || '-'}</div>
                                                        </td>
                                                        <td className="hidden lg:table-cell px-6 py-4">
                                                            <div className="text-sm text-gray-600 truncate max-w-xs">{contact.email || '-'}</div>
                                                        </td>
                                                        <td className="hidden xl:table-cell px-6 py-4">
                                                            <div className="text-sm text-gray-600">{contact.phone || '-'}</div>
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            <button
                                                                onClick={(e) => handleRemoveContact(contact.id, e)}
                                                                className="p-2 bg-red-100 hover:bg-red-200 rounded-lg text-red-600 transition-colors inline-flex items-center"
                                                                title={t.dashboard?.remove || 'Remove'}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="mt-8 flex items-center justify-center gap-2">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>

                                    <div className="flex gap-2">
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                                    currentPage === page
                                                        ? 'bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                    </div>

                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Debug Panel - Admin only */}
            {isAdmin && (
            <div className="mt-8 border-t border-gray-200 pt-4">
                <button
                    onClick={() => setShowDebugPanel(!showDebugPanel)}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                    <Bug className="w-4 h-4" />
                    <span>Debug localStorage</span>
                    {showDebugPanel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {showDebugPanel && (
                    <div className="mt-4 bg-gray-900 text-green-400 rounded-lg p-4 overflow-x-auto">
                        {/* User Info */}
                        <div className="mb-4 p-3 bg-gray-800 rounded">
                            <div className="flex justify-between items-center mb-2">
                                <h4 className="text-sm font-mono text-white">User Info</h4>
                                <button
                                    onClick={async () => {
                                        const addLog = (msg, type = 'info') => setDebugLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message: msg, type }]);
                                        const userUuid = localStorage.getItem('userUuid');
                                        const authToken = localStorage.getItem('authToken');
                                        if (!userUuid) {
                                            addLog('ERROR: userUuid not found in localStorage', 'error');
                                            return;
                                        }
                                        if (!authToken) {
                                            addLog('ERROR: authToken not found in localStorage', 'error');
                                            return;
                                        }
                                        addLog(`Fetching user info for UUID: ${userUuid}...`);
                                        addLog(`Token: ${authToken.substring(0, 20)}...`);
                                        try {
                                            const response = await fetch(`https://apicabaza.creago.be/rest/cabaza/peoples/${userUuid}`, {
                                                headers: {
                                                    'Authorization': `Bearer ${authToken}`,
                                                    'Content-Type': 'application/json'
                                                }
                                            });
                                            addLog(`Response status: ${response.status} ${response.statusText}`);

                                            const text = await response.text();
                                            addLog(`Response length: ${text.length} chars`);

                                            if (!text || text.length === 0) {
                                                addLog('ERROR: Empty response from API', 'error');
                                                return;
                                            }

                                            let result;
                                            try {
                                                result = JSON.parse(text);
                                            } catch (parseErr) {
                                                addLog(`ERROR: Invalid JSON - ${text.substring(0, 100)}`, 'error');
                                                return;
                                            }

                                            addLog(`API response: ${JSON.stringify(result).substring(0, 150)}...`);
                                            if (result.success || result.SUCCESS) {
                                                const data = result.data || result.DATA;
                                                const userObj = {
                                                    id: userId,
                                                    email: data.email || localStorage.getItem('userEmail'),
                                                    firstName: data.firstname || data.firstName,
                                                    lastName: data.lastname || data.lastName,
                                                    peopleUuid: data.peopleuuid || data.peopleUuid || userUuid,
                                                    storageKey: data.storagekey || data.storageKey
                                                };
                                                localStorage.setItem('cabaza_user', JSON.stringify(userObj));
                                                addLog(`SUCCESS: cabaza_user created with storageKey: ${userObj.storageKey ? 'present' : 'MISSING'}`, userObj.storageKey ? 'success' : 'error');
                                                // Force re-render
                                                setLocalStorageContacts([...localStorageContacts]);
                                            } else {
                                                addLog(`ERROR: ${result.message || result.MESSAGE}`, 'error');
                                            }
                                        } catch (err) {
                                            addLog(`ERROR: ${err.message}`, 'error');
                                        }
                                    }}
                                    className="text-xs bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-500"
                                >
                                    Fix User Info
                                </button>
                            </div>
                            {(() => {
                                const userJson = localStorage.getItem('cabaza_user');
                                const user = userJson ? JSON.parse(userJson) : null;
                                return user ? (
                                    <div className="text-xs font-mono space-y-1">
                                        <div><span className="text-gray-400">userId:</span> <span className="text-cyan-400">{userId}</span></div>
                                        <div><span className="text-gray-400">peopleUuid:</span> <span className="text-purple-400">{user.peopleUuid || user.peopleuuid || 'MISSING'}</span></div>
                                        <div><span className="text-gray-400">storageKey:</span> <span className={user.storageKey ? 'text-green-400' : 'text-red-400'}>{user.storageKey ? user.storageKey.substring(0, 16) + '...' : 'MISSING'}</span></div>
                                    </div>
                                ) : (
                                    <div className="text-xs font-mono space-y-1">
                                        <p className="text-red-400">cabaza_user not found in localStorage</p>
                                        <p className="text-gray-400">userUuid: {localStorage.getItem('userUuid') || 'MISSING'}</p>
                                        <p className="text-yellow-400">Click "Fix User Info" to fetch from API</p>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Debug Logs */}
                        {debugLogs.length > 0 && (
                            <div className="mb-4 p-3 bg-gray-800 rounded max-h-40 overflow-y-auto">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="text-sm font-mono text-white">Debug Logs</h4>
                                    <button onClick={() => setDebugLogs([])} className="text-xs text-gray-400 hover:text-white">Clear</button>
                                </div>
                                <div className="text-xs font-mono space-y-1">
                                    {debugLogs.map((log, i) => (
                                        <div key={i} className={log.type === 'error' ? 'text-red-400' : log.type === 'success' ? 'text-green-400' : 'text-gray-300'}>
                                            [{log.time}] {log.message}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-mono text-white">
                                localStorage[{userId}] - {localStorageContacts.length} contacts
                            </h4>
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    onClick={async () => {
                                        const addLog = (msg, type = 'info') => setDebugLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message: msg, type }]);
                                        const userJson = localStorage.getItem('cabaza_user');
                                        if (!userJson) { addLog('ERROR: cabaza_user not found', 'error'); return; }
                                        const user = JSON.parse(userJson);
                                        const peopleuuid = user.peopleUuid || user.peopleuuid;
                                        const storageKey = user.storageKey;
                                        if (!peopleuuid || !storageKey) { addLog('ERROR: peopleuuid or storageKey missing', 'error'); return; }

                                        addLog('Loading from server file...');
                                        try {
                                            const serverContacts = await loadContactsFromFile(peopleuuid, storageKey);
                                            addLog(`Server response: ${JSON.stringify(serverContacts).substring(0, 200)}...`);
                                            if (serverContacts && serverContacts.length > 0) {
                                                addLog(`SUCCESS: Loaded ${serverContacts.length} contacts from server`, 'success');
                                                // Update localStorage and display
                                                const STORAGE_KEY = 'cabaza_user_contacts';
                                                const data = localStorage.getItem(STORAGE_KEY);
                                                const allContacts = data ? JSON.parse(data) : {};
                                                allContacts[userId] = serverContacts;
                                                localStorage.setItem(STORAGE_KEY, JSON.stringify(allContacts));
                                                setLocalStorageContacts(serverContacts);
                                                setMyContacts(serverContacts);
                                                addLog(`Updated localStorage and display with ${serverContacts.length} contacts`, 'success');
                                            } else {
                                                addLog('Server file empty or not found', 'error');
                                            }
                                        } catch (err) {
                                            addLog(`ERROR: ${err.message}`, 'error');
                                        }
                                    }}
                                    className="text-xs bg-blue-700 text-white px-2 py-1 rounded hover:bg-blue-600"
                                >
                                    Test Load
                                </button>
                                <button
                                    onClick={async () => {
                                        const addLog = (msg, type = 'info') => setDebugLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message: msg, type }]);
                                        const userJson = localStorage.getItem('cabaza_user');
                                        if (!userJson) { addLog('ERROR: cabaza_user not found', 'error'); return; }
                                        const user = JSON.parse(userJson);
                                        const peopleuuid = user.peopleUuid || user.peopleuuid;
                                        const storageKey = user.storageKey;
                                        if (!peopleuuid || !storageKey) { addLog('ERROR: peopleuuid or storageKey missing', 'error'); return; }

                                        addLog('Saving to server file...');
                                        addLog(`peopleuuid: ${peopleuuid}`);
                                        addLog(`storageKey: ${storageKey.substring(0, 16)}...`);
                                        addLog(`contacts count: ${localStorageContacts.length}`);
                                        try {
                                            const result = await saveContactsToFile(peopleuuid, storageKey, localStorageContacts);
                                            addLog(`Save result: ${JSON.stringify(result)}`);
                                            const success = result.success || result.SUCCESS;
                                            const message = result.message || result.MESSAGE;
                                            if (success) {
                                                addLog('SUCCESS: Contacts saved to server', 'success');
                                            } else {
                                                addLog(`ERROR: ${message}`, 'error');
                                            }
                                        } catch (err) {
                                            addLog(`ERROR: ${err.message}`, 'error');
                                        }
                                    }}
                                    className="text-xs bg-purple-700 text-white px-2 py-1 rounded hover:bg-purple-600"
                                >
                                    Test Save
                                </button>
                                <button
                                    onClick={async () => {
                                        const addLog = (msg, type = 'info') => setDebugLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message: msg, type }]);
                                        const userJson = localStorage.getItem('cabaza_user');
                                        if (!userJson) { addLog('ERROR: cabaza_user not found', 'error'); return; }
                                        const user = JSON.parse(userJson);
                                        const peopleuuid = user.peopleUuid || user.peopleuuid;
                                        const storageKey = user.storageKey;

                                        localStorage.removeItem('cabaza_user_contacts');
                                        clearContactsCache(userId);
                                        setLoading(true);

                                        try {
                                            // First try to load from encrypted server file (has full contact data)
                                            if (peopleuuid && storageKey) {
                                                addLog('Loading from encrypted server file...');
                                                const serverContacts = await loadContactsFromFile(peopleuuid, storageKey);
                                                if (serverContacts && serverContacts.length > 0) {
                                                    addLog(`Loaded ${serverContacts.length} contacts from server file`, 'success');
                                                    // Update localStorage
                                                    const STORAGE_KEY = 'cabaza_user_contacts';
                                                    const allContacts = {};
                                                    allContacts[userId] = serverContacts;
                                                    localStorage.setItem(STORAGE_KEY, JSON.stringify(allContacts));
                                                    setLocalStorageContacts(serverContacts);
                                                    setMyContacts(serverContacts);
                                                    setLoading(false);
                                                    return;
                                                }
                                                addLog('No server file found, falling back to API...');
                                            }

                                            // Fallback to API if no server file
                                            addLog('Loading from API...');
                                            const contacts = await getUserContacts(userId, true);
                                            setMyContacts(contacts);
                                            const data = localStorage.getItem('cabaza_user_contacts');
                                            const allContacts = data ? JSON.parse(data) : {};
                                            const userData = allContacts[userId];
                                            setLocalStorageContacts(Array.isArray(userData) ? userData : (userData?.contacts || []));
                                            addLog(`Loaded ${contacts.length} contacts from API`, 'success');

                                            // Save to server file for next time
                                            if (peopleuuid && storageKey && contacts.length > 0) {
                                                addLog('Saving to server file...');
                                                const saveResult = await saveContactsToFile(peopleuuid, storageKey, contacts);
                                                const success = saveResult.success || saveResult.SUCCESS;
                                                addLog(success ? 'Saved to server file' : 'Failed to save to server', success ? 'success' : 'error');
                                            }
                                        } catch (err) {
                                            addLog(`ERROR: ${err.message}`, 'error');
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                    className="text-xs bg-green-700 text-white px-2 py-1 rounded hover:bg-green-600 flex items-center gap-1"
                                >
                                    <RefreshCw className="w-3 h-3" />
                                    Full Resync
                                </button>
                            </div>
                        </div>

                        {localStorageContacts.length === 0 ? (
                            <p className="text-yellow-400 text-sm font-mono">Aucun contact dans le localStorage</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs font-mono">
                                    <thead>
                                        <tr className="text-gray-400 border-b border-gray-700">
                                            <th className="text-left p-2">#</th>
                                            <th className="text-left p-2">id</th>
                                            <th className="text-left p-2">localuuid</th>
                                            <th className="text-left p-2">peopleuuid</th>
                                            <th className="text-left p-2">contactType</th>
                                            <th className="text-left p-2">firstName</th>
                                            <th className="text-left p-2">lastName</th>
                                            <th className="text-left p-2">email</th>
                                            <th className="text-left p-2">phone</th>
                                            <th className="text-left p-2">company</th>
                                            <th className="text-left p-2">rating</th>
                                            <th className="text-left p-2">createdAt</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(Array.isArray(localStorageContacts) ? localStorageContacts : []).map((contact, index) => (
                                            <tr key={contact.localuuid || contact.id || index} className="border-b border-gray-800 hover:bg-gray-800">
                                                <td className="p-2 text-gray-500">{index + 1}</td>
                                                <td className="p-2 text-cyan-400">{contact.id || '-'}</td>
                                                <td className="p-2 text-purple-400 max-w-[100px] truncate" title={contact.localuuid}>
                                                    {contact.localuuid ? contact.localuuid.substring(0, 8) + '...' : '-'}
                                                </td>
                                                <td className="p-2 max-w-[100px] truncate" title={contact.peopleuuid}>
                                                    {contact.peopleuuid ? (
                                                        <span className="text-green-400">{contact.peopleuuid.substring(0, 8)}...</span>
                                                    ) : (
                                                        <span className="text-gray-600">null</span>
                                                    )}
                                                </td>
                                                <td className="p-2">
                                                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                                                        contact.contactType === 'PUBLIC'
                                                            ? 'bg-green-900 text-green-300'
                                                            : 'bg-gray-700 text-gray-300'
                                                    }`}>
                                                        {contact.contactType || (contact.peopleuuid ? 'PUBLIC' : 'CONTACT')}
                                                    </span>
                                                </td>
                                                <td className="p-2 text-white">{contact.firstName || '-'}</td>
                                                <td className="p-2 text-white">{contact.lastName || '-'}</td>
                                                <td className="p-2 text-blue-400 max-w-[150px] truncate" title={contact.email}>
                                                    {contact.email || '-'}
                                                </td>
                                                <td className="p-2 text-gray-300">{contact.phone || contact.gsm || '-'}</td>
                                                <td className="p-2 text-gray-300 max-w-[100px] truncate" title={contact.company}>
                                                    {contact.company || '-'}
                                                </td>
                                                <td className="p-2 text-yellow-400">{contact.rating || 0}</td>
                                                <td className="p-2 text-gray-500 text-xs">
                                                    {contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Server File Table */}
                        <div className="mt-6 border-t border-gray-700 pt-4">
                            <h4 className="text-sm font-mono text-white mb-3">
                                Server File (private/contacts.json) - {serverFileContacts.length} contacts
                            </h4>
                            {serverFileContacts.length === 0 ? (
                                <p className="text-yellow-400 text-sm font-mono">Aucun contact dans le fichier serveur</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs font-mono">
                                        <thead>
                                            <tr className="text-orange-400 border-b border-gray-700">
                                                <th className="text-left p-2">#</th>
                                                <th className="text-left p-2">id</th>
                                                <th className="text-left p-2">localuuid</th>
                                                <th className="text-left p-2">peopleuuid</th>
                                                <th className="text-left p-2">contactType</th>
                                                <th className="text-left p-2">firstName</th>
                                                <th className="text-left p-2">lastName</th>
                                                <th className="text-left p-2">email</th>
                                                <th className="text-left p-2">phone</th>
                                                <th className="text-left p-2">company</th>
                                                <th className="text-left p-2">rating</th>
                                                <th className="text-left p-2">createdAt</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(Array.isArray(serverFileContacts) ? serverFileContacts : []).map((contact, index) => (
                                                <tr key={contact.localuuid || contact.id || index} className="border-b border-gray-800 hover:bg-gray-800">
                                                    <td className="p-2 text-gray-500">{index + 1}</td>
                                                    <td className="p-2 text-cyan-400">{contact.id || '-'}</td>
                                                    <td className="p-2 text-purple-400 max-w-[100px] truncate" title={contact.localuuid}>
                                                        {contact.localuuid ? contact.localuuid.substring(0, 8) + '...' : '-'}
                                                    </td>
                                                    <td className="p-2 max-w-[100px] truncate" title={contact.peopleuuid}>
                                                        {contact.peopleuuid ? (
                                                            <span className="text-green-400">{contact.peopleuuid.substring(0, 8)}...</span>
                                                        ) : (
                                                            <span className="text-gray-600">null</span>
                                                        )}
                                                    </td>
                                                    <td className="p-2">
                                                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                                                            contact.contactType === 'PUBLIC'
                                                                ? 'bg-green-900 text-green-300'
                                                                : 'bg-gray-700 text-gray-300'
                                                        }`}>
                                                            {contact.contactType || (contact.peopleuuid ? 'PUBLIC' : 'CONTACT')}
                                                        </span>
                                                    </td>
                                                    <td className="p-2 text-orange-300">{contact.firstName || '-'}</td>
                                                    <td className="p-2 text-orange-300">{contact.lastName || '-'}</td>
                                                    <td className="p-2 text-blue-400 max-w-[150px] truncate" title={contact.email}>
                                                        {contact.email || '-'}
                                                    </td>
                                                    <td className="p-2 text-gray-300">{contact.phone || contact.gsm || '-'}</td>
                                                    <td className="p-2 text-gray-300 max-w-[100px] truncate" title={contact.company}>
                                                        {contact.company || '-'}
                                                    </td>
                                                    <td className="p-2 text-yellow-400">{contact.rating || 0}</td>
                                                    <td className="p-2 text-gray-500 text-xs">
                                                        {contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Raw JSON view */}
                        <details className="mt-4">
                            <summary className="text-gray-400 text-xs cursor-pointer hover:text-white">
                                Voir JSON brut localStorage
                            </summary>
                            <pre className="mt-2 text-xs text-gray-300 bg-gray-800 p-2 rounded overflow-x-auto max-h-60">
                                {JSON.stringify(localStorageContacts, null, 2)}
                            </pre>
                        </details>
                        <details className="mt-2">
                            <summary className="text-orange-400 text-xs cursor-pointer hover:text-white">
                                Voir JSON brut Server File
                            </summary>
                            <pre className="mt-2 text-xs text-orange-300 bg-gray-800 p-2 rounded overflow-x-auto max-h-60">
                                {JSON.stringify(serverFileContacts, null, 2)}
                            </pre>
                        </details>
                    </div>
                )}
            </div>
            )}

            {/* Delete Contact Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirmModal.isOpen}
                onClose={() => setDeleteConfirmModal({ isOpen: false, contact: null })}
                onConfirm={confirmDeleteContact}
                title={t.dashboard?.deleteContactTitle || 'Delete contact'}
                message={t.dashboard?.deleteContactWarning || 'Are you sure you want to delete this contact?'}
                confirmText={t.dashboard?.confirmDelete || 'Delete'}
                variant="danger"
            >
                <div className="bg-gray-100 rounded-lg p-3 mb-4">
                    <p className="font-semibold text-gray-900">
                        {deleteConfirmModal.contact?.firstName} {deleteConfirmModal.contact?.lastName}
                    </p>
                    {deleteConfirmModal.contact?.email && (
                        <p className="text-sm text-gray-600">{deleteConfirmModal.contact.email}</p>
                    )}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-amber-800 font-medium text-sm">
                                {t.dashboard?.deleteContactInfo || 'This will permanently remove the contact from your entire contact system.'}
                            </p>
                            <p className="text-amber-700 text-sm mt-2">
                                {t.dashboard?.deleteContactTip || 'To remove a contact from a group only, use the'}
                                <span className="inline-flex items-center mx-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded">
                                    <UserMinus className="w-3 h-3 mr-1" />
                                    {t.dashboard?.removeFromGroupIcon || 'remove from group'}
                                </span>
                                {t.dashboard?.deleteContactTipEnd || 'icon instead.'}
                            </p>
                        </div>
                    </div>
                </div>
            </ConfirmModal>

            {/* Delete Group Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteGroupModal.isOpen}
                onClose={() => setDeleteGroupModal({ isOpen: false, group: null })}
                onConfirm={confirmDeleteGroup}
                title={t.groups?.deleteGroupTitle || 'Delete group'}
                message={t.groups?.deleteGroupWarning || 'Are you sure you want to delete this group?'}
                confirmText={t.groups?.confirmDeleteGroup || 'Delete group'}
                icon={Users}
                variant="theme"
            >
                <div className="bg-gradient-to-r from-cabaza-purple/10 to-cabaza-pink/10 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-cabaza-purple/20 rounded-full">
                            <Users className="w-5 h-5 text-cabaza-purple" />
                        </div>
                        <div>
                            <p className="font-semibold text-gray-900">
                                {getGroupName(deleteGroupModal.group, language)}
                            </p>
                            <p className="text-sm text-gray-600">
                                {(deleteGroupModal.group?.memberCount || 0)} {t.groups?.members || 'member(s)'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Info about members */}
                <div className={`rounded-lg p-4 ${
                    (deleteGroupModal.group?.memberCount || 0) > 0
                        ? 'bg-amber-50 border border-amber-200'
                        : 'bg-blue-50 border border-blue-200'
                }`}>
                    <div className="flex gap-3">
                        <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            (deleteGroupModal.group?.memberCount || 0) > 0
                                ? 'text-amber-600'
                                : 'text-blue-600'
                        }`} />
                        <div>
                            <p className={`font-medium text-sm ${
                                (deleteGroupModal.group?.memberCount || 0) > 0
                                    ? 'text-amber-800'
                                    : 'text-blue-800'
                            }`}>
                                {(deleteGroupModal.group?.memberCount || 0) > 0
                                    ? (t.groups?.deleteGroupInfoWithMembers || 'This group contains {count} member(s) who will be removed from the group.').replace('{count}', deleteGroupModal.group?.memberCount || 0)
                                    : (t.groups?.deleteGroupInfoNoMembers || 'This group has no members.')
                                }
                            </p>
                            <p className={`text-sm mt-2 ${
                                (deleteGroupModal.group?.memberCount || 0) > 0
                                    ? 'text-amber-700'
                                    : 'text-blue-700'
                            }`}>
                                {t.groups?.deleteGroupNote || 'Contacts will not be deleted from your list, they will only be removed from this group.'}
                            </p>
                        </div>
                    </div>
                </div>
            </ConfirmModal>

            {/* Create Group Modal */}
            <CreateGroupModal
                isOpen={showCreateGroupModal}
                onClose={() => setShowCreateGroupModal(false)}
                contacts={myContacts}
                userEmail={userEmail}
                userId={userId}
                onGroupCreated={handleGroupCreated}
            />

            {/* Add Members Modal */}
            <AddMembersModal
                isOpen={showAddMembersModal}
                onClose={() => setShowAddMembersModal(false)}
                group={selectedGroup}
                contacts={myContacts}
                onMembersAdded={handleMembersAdded}
            />
        </div>
    );
};

export default MyContacts;
